/**
 * Simple API cache utility to prevent duplicate API calls
 */
class ApiCache {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
  }

  /**
   * Generate cache key from URL and params
   */
  generateKey(url, params = {}) {
    return `${url}?${JSON.stringify(params)}`;
  }

  /**
   * Check if request is pending
   */
  isPending(key) {
    return this.pendingRequests.has(key);
  }

  /**
   * Add pending request
   */
  addPending(key, promise) {
    this.pendingRequests.set(key, promise);
    return promise;
  }

  /**
   * Remove pending request
   */
  removePending(key) {
    this.pendingRequests.delete(key);
  }

  /**
   * Get cached data
   */
  get(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  /**
   * Set cached data
   */
  set(key, data, ttl = 30000) { // 30 seconds default
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * Clear cache
   */
  clear() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Execute API call with caching and deduplication
   */
  async execute(key, apiCall, ttl = 30000) {
    // Check cache first
    const cached = this.get(key);
    if (cached) {
      return cached;
    }

    // Check if request is pending
    if (this.isPending(key)) {
      return this.pendingRequests.get(key);
    }

    // Execute new request
    const promise = apiCall().then(result => {
      this.set(key, result, ttl);
      this.removePending(key);
      return result;
    }).catch(error => {
      this.removePending(key);
      throw error;
    });

    this.addPending(key, promise);
    return promise;
  }
}

// Export singleton instance
export const apiCache = new ApiCache(); 